import { useEffect, useRef } from 'react';

// Open layers, bottom to top: only the top one answers Escape.
const stack = [];

/**
 * What every overlay owes the keyboard: Escape closes the top layer, focus moves
 * into the panel on open and back to the trigger on close, and everything
 * outside the layer is `inert` so Tab cannot wander behind it.
 *
 * `layerRef` is the overlay's outermost element, which must be a direct child of
 * <body> (a portal) for the inert sweep to leave it alone.
 */
const useModalLayer = (isOpen, onClose, layerRef, panelRef = layerRef) => {
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    useEffect(() => {
        if (!isOpen) return undefined;
        const token = {};
        stack.push(token);
        const trigger = document.activeElement;

        // The layer mounts `visibility: hidden` and turns visible a frame or two
        // later, and a hidden element can't take focus — so keep trying until it
        // lands. A field marked data-autofocus gets it; otherwise the panel does.
        const panel = panelRef.current;
        let raf = 0;
        let tries = 0;
        const place = () => {
            if (!panel || panel.contains(document.activeElement)) return;
            const target = panel.querySelector('[data-autofocus]') || panel;
            if (target === panel && !panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
            if (!panel.contains(document.activeElement) && ++tries < 30) raf = requestAnimationFrame(place);
        };
        place();

        const layer = layerRef.current;
        const others = [...document.body.children].filter((el) => el !== layer && !el.inert && el.tagName !== 'SCRIPT');
        others.forEach((el) => { el.inert = true; });

        const onKey = (e) => {
            if (e.key === 'Escape' && stack[stack.length - 1] === token) {
                e.stopPropagation();
                closeRef.current?.();
            }
        };
        document.addEventListener('keydown', onKey);

        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('keydown', onKey);
            others.forEach((el) => { el.inert = false; });
            stack.splice(stack.indexOf(token), 1);
            if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
        };
    }, [isOpen, layerRef, panelRef]);
};

export default useModalLayer;
